import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { useLineupUsage } from "../../hooks/use-lineup-usage";
import { PLAYER_HEADSHOT_FALLBACK } from "../../../../config/constants";

const clampNumber = (v) => (Number.isFinite(v) ? v : null);

const formatAvg = (v) => {
  const n = clampNumber(v);
  if (n === null) return "—";
  const s = n.toFixed(3);
  return s.startsWith("0.") ? s.replace("0.", ".") : s;
};

const formatWar = (v) => {
  const n = clampNumber(v);
  return n === null ? "—" : n.toFixed(1);
};

const getRoleInfo = (gamesPlayed, totalGames) => {
  const tg = Math.max(1, clampNumber(totalGames) ?? 1);
  const gp = clampNumber(gamesPlayed) ?? 0;
  const pct = gp / tg;
  if (pct >= 0.8) return { role: "Everyday", pill: "bg-emerald-50 text-emerald-700 ring-emerald-200" };
  if (pct >= 0.5) return { role: "Platoon", pill: "bg-blue-50 text-blue-700 ring-blue-200" };
  if (pct >= 0.1) return { role: "Rotation", pill: "bg-gray-50 text-gray-700 ring-gray-200" };
  return { role: "Bench", pill: "bg-slate-50 text-slate-700 ring-slate-200" };
};

const getLineupSpot = (avgOrder) => {
  const n = clampNumber(avgOrder);
  if (n === null) return null;
  const rounded = Math.round(n);
  if (rounded <= 2) return { label: "Top", pill: "bg-amber-50 text-amber-700 ring-amber-200" };
  if (rounded === 3) return { label: "3-Hole", pill: "bg-rose-50 text-rose-700 ring-rose-200" };
  if (rounded === 4) return { label: "Cleanup", pill: "bg-red-50 text-red-700 ring-red-200" };
  if (rounded <= 6) return { label: "Middle", pill: "bg-blue-50 text-blue-700 ring-blue-200" };
  return { label: "Bottom", pill: "bg-slate-50 text-slate-700 ring-slate-200" };
};

const Pill = ({ children, className }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${className}`}>
    {children}
  </span>
);

const PositionBadge = memo(function PositionBadge({ position }) {
  if (!position) return null;
  const pos = String(position).split("/")[0].split(",")[0].trim().toUpperCase();
  const colors = {
    C: "bg-amber-50 text-amber-700 ring-amber-200",
    INF: "bg-blue-50 text-blue-700 ring-blue-200",
    "1B": "bg-blue-50 text-blue-700 ring-blue-200",
    "2B": "bg-blue-50 text-blue-700 ring-blue-200",
    SS: "bg-blue-50 text-blue-700 ring-blue-200",
    "3B": "bg-blue-50 text-blue-700 ring-blue-200",
    OF: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    LF: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    CF: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    RF: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    DH: "bg-gray-50 text-gray-700 ring-gray-200",
    UT: "bg-purple-50 text-purple-700 ring-purple-200",
  };
  const cls = colors[pos] || "bg-gray-50 text-gray-700 ring-gray-200";
  const label = String(position).length > 8 ? String(position).slice(0, 8) : String(position);
  return <Pill className={cls}>{label}</Pill>;
});

const Headshot = memo(function Headshot({ src, name }) {
  return (
    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200">
      <img
        src={src || PLAYER_HEADSHOT_FALLBACK}
        alt={name}
        className="h-full w-full object-cover object-top"
        onError={(e) => {
          if (e.currentTarget.src !== PLAYER_HEADSHOT_FALLBACK) e.currentTarget.src = PLAYER_HEADSHOT_FALLBACK;
        }}
        loading="lazy"
      />
    </div>
  );
});

const PlayerRow = memo(function PlayerRow({ player, index, totalGames, variant, onClick }) {
  const role = getRoleInfo(player.games, totalGames);
  const spot = getLineupSpot(player.avg_order);

  if (variant === "bench") {
    return (
      <div
        onClick={onClick}
        className="group flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3 transition-all hover:border-blue-300 hover:shadow-sm cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <Headshot src={player.img_url} name={player.player_name} />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm text-gray-900 group-hover:text-blue-600 break-words">
              {player.player_name || "Unknown Player"}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <PositionBadge position={player.position} />
              {spot ? <Pill className={spot.pill}>{spot.label}</Pill> : null}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs">
          <div className="text-gray-500">
            {player.games ?? 0} G{player.pa != null ? ` • ${player.pa} PA` : ""}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="font-semibold text-gray-900">{formatAvg(player.ba)}</div>
              <div className="text-[10px] text-gray-400">AVG</div>
            </div>
            <div className="text-center min-w-[32px]">
              <div className="font-semibold text-gray-900">{player.hr ?? "—"}</div>
              <div className="text-[10px] text-gray-400">HR</div>
            </div>
            <div className="text-center min-w-[36px]">
              <div className="font-semibold text-gray-900">{formatWar(player.war)}</div>
              <div className="text-[10px] text-gray-400">WAR</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-3 transition-all hover:border-blue-200 hover:shadow-sm cursor-pointer"
    >
      <div className="w-6 text-center text-lg font-bold text-gray-200">{index}</div>
      <Headshot src={player.img_url} name={player.player_name} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-medium text-gray-900 group-hover:text-blue-600">
            {player.player_name || "Unknown Player"}
          </div>
          <PositionBadge position={player.position} />
          {spot ? <Pill className={spot.pill}>{spot.label}</Pill> : null}
          <Pill className={role.pill}>{role.role}</Pill>
        </div>
        <div className="mt-0.5 text-xs text-gray-500">
          {player.games ?? 0} G{player.pa != null ? ` • ${player.pa} PA` : ""}
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-center">
          <div className="text-sm font-semibold text-gray-900">{formatAvg(player.ba)}</div>
          <div className="text-[10px] font-medium tracking-wide text-gray-400">AVG</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-semibold text-gray-900">{player.hr ?? "—"}</div>
          <div className="text-[10px] font-medium tracking-wide text-gray-400">HR</div>
        </div>
        <div className="min-w-[44px] text-center">
          <div className="text-sm font-semibold text-gray-900">{formatWar(player.war)}</div>
          <div className="text-[10px] font-medium tracking-wide text-gray-400">WAR</div>
        </div>
      </div>
    </div>
  );
});

export const LineupUsage = ({ teamId, years }) => {
  const navigate = useNavigate();
  const { status, selectedYear, setSelectedYear, availableYears, starters, bench, totalGames } = useLineupUsage({
    teamId,
    years,
  });

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
      </div>
    );
  }

  if (status === "error") {
    return <div className="py-12 text-center text-sm text-gray-500">Couldn&apos;t load lineup usage.</div>;
  }

  if (!starters.length && !bench.length) {
    return <div className="py-12 text-center text-sm text-gray-500">No lineup data available.</div>;
  }

  return (
    <div className="p-2 pl-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {availableYears.length > 0 ? (
          <div className="flex items-center gap-2">
            <div className="text-xs font-medium text-gray-500">Year</div>
            <select
              value={selectedYear ?? ""}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableYears.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {starters.length > 0 ? (
        <div className="mb-8">
          <div className="grid gap-2">
            {starters.map((b, idx) => (
              <PlayerRow
                key={b.batter_id}
                player={b}
                index={idx + 1}
                totalGames={totalGames}
                variant="starter"
                onClick={() => navigate(`/player/${b.batter_id}`)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {bench.length > 0 ? (
        <div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {bench.map((b) => (
              <PlayerRow
                key={b.batter_id}
                player={b}
                totalGames={totalGames}
                variant="bench"
                onClick={() => navigate(`/player/${b.batter_id}`)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
